<div class="mt-8 mb-4 max-w-2xl mx-auto px-4">
    <div class="flex justify-between items-center w-full mb-4">
        <button (click)="exitQuiz()" class="flex items-center text-gray-500 hover:text-red-500 font-medium transition-colors">
            <i class="fa fa-arrow-left mr-2"></i> Exit Quiz
        </button>
    </div>

    @if (session(); as s) {
    <div class="flex flex-col mb-4 border-0 w-full">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
                <h1 class="font-semibold text-2xl text-primary-500">
                    Live Quiz Participation
                </h1>
                <span class="text-sm text-gray-500">
                    {{ s.status === quizStatus.waiting ? 'Waiting for the host to start' :
                    s.status === quizStatus.inProgress ? 'Quiz in progress' : s.status === quizStatus.completed ?
                    'Quiz completed' : '' }}
                </span>
            </div>
            <div class="mt-2 md:mt-0 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100 flex items-center gap-2">
                <i class="fa fa-user-circle text-blue-500"></i>
                <span class="text-xs font-semibold text-blue-600 uppercase tracking-wider">{{ user?.name }}</span>
            </div>
        </div>

        <!-- WAITING VIEW -->
        @if (s.status === quizStatus.waiting) {
        <div class="flex flex-col items-center justify-center bg-white rounded-xl shadow-lg border border-gray-100 p-10 text-center animate-fade-in">
            <div class="w-20 h-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-6 animate-pulse">
                <i class="fa fa-clock text-4xl"></i>
            </div>
            <h2 class="text-2xl font-bold text-gray-800">You're Checked In!</h2>
            <p class="text-gray-500 mt-2 max-w-md">
                The quiz hasn't started yet. Hang tight while the host prepares everything.
            </p>
        </div>
        }

        <!-- COMPLETED VIEW -->
        @else if (s.status === quizStatus.completed) {
        <div class="flex flex-col items-center justify-center bg-white rounded-xl shadow-lg border border-gray-100 p-10 text-center animate-fade-in">
            <div class="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 animate-bounce">
                <i class="fa fa-trophy text-4xl"></i>
            </div>
            <h2 class="text-2xl font-bold text-gray-800">Quiz Completed!</h2>
            <p class="text-gray-500 mt-2">Great job! You finished the quiz session.</p>
            <div class="mt-6 bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 flex flex-col items-center">
                <span class="text-sm text-gray-400 font-semibold uppercase tracking-wider">Your Total Score</span>
                <span class="text-5xl font-black text-primary-500 mt-1">{{ totalScore() }}</span>
            </div>
            <button (click)="exitQuiz()" class="mt-8 px-6 py-2.5 bg-primary-500 text-white font-semibold rounded-lg shadow hover:bg-primary-600 transition-colors">
                Back to Dashboard
            </button>
        </div>
        }

        <!-- ACTIVE QUESTION VIEW -->
        @else if (s.status === quizStatus.inProgress) {
        <div class="flex flex-col gap-6">
            <!-- Header Stats -->
            <div class="flex justify-between items-center bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm">
                <span class="text-sm font-semibold text-gray-600">Question {{ s.currentIndex + 1 }} of {{ s.displayTotal || quiz?.questions?.length }}</span>
                <span class="text-sm font-bold text-primary-500">Score: {{ totalScore() }}</span>
            </div>

            <!-- Timer / Submitted States -->
            @if (isSubmitted()) {
            <div class="flex flex-col items-center justify-center bg-white rounded-xl shadow-lg border border-gray-100 p-10 text-center">
                <div class="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4 animate-spin">
                    <i class="fa fa-spinner text-2xl"></i>
                </div>
                <h3 class="text-xl font-bold text-gray-800">Answer Submitted!</h3>
                <p class="text-gray-500 mt-2">Waiting for the host to move to the next question...</p>
            </div>
            }

            @else if (remainingTime() === 0) {
            <div class="flex flex-col items-center justify-center bg-white rounded-xl shadow-lg border border-gray-100 p-10 text-center">
                <div class="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4 animate-pulse">
                    <i class="fa fa-hourglass-end text-2xl"></i>
                </div>
                <h3 class="text-xl font-bold text-gray-800">Time's Up!</h3>
                <p class="text-gray-500 mt-2">You didn't submit in time. Getting ready for the next question...</p>
            </div>
            }

            @else {
            <!-- Question Content Card -->
            @let q = currentQuestion();
            @if (q) {
            <div class="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <!-- Timer Bar -->
                <div class="h-2 w-full bg-gray-100">
                    <div class="h-full transition-all duration-1000 ease-linear"
                        [class.bg-red-500]="remainingTime() <= 5"
                        [class.bg-green-500]="remainingTime() > 5"
                        [style.width.%]="(remainingTime() / (s.durationPerQuestion || 30)) * 100">
                    </div>
                </div>

                <div class="p-6">
                    <div class="flex justify-between items-center mb-4">
                        <span class="text-xs font-semibold text-gray-400 uppercase tracking-widest">{{ q.questionType }}</span>
                        <div class="flex items-center gap-1.5 text-sm font-bold"
                            [class.text-red-500]="remainingTime() <= 5"
                            [class.text-primary-500]="remainingTime() > 5">
                            <i class="fa fa-clock"></i>
                            <span>{{ remainingTime() }}s</span>
                        </div>
                    </div>

                    <h2 class="text-xl font-medium text-gray-800 mb-6 leading-relaxed">{{ q.questionText }}</h2>

                    <!-- Multiple Choice -->
                    @if (q.questionType === questionTypes.multipleChoice) {
                    <div class="flex flex-col gap-3">
                        @for (opt of q.options; track opt.id) {
                        <button (click)="selectOption(opt.id, opt.text)"
                            class="w-full text-left px-5 py-4 border rounded-xl font-medium transition-all duration-200 focus:outline-none flex items-center justify-between"
                            [class.bg-primary-50]="selectedOptionId() === opt.id"
                            [class.border-primary-500]="selectedOptionId() === opt.id"
                            [class.text-primary-800]="selectedOptionId() === opt.id"
                            [class.bg-white]="selectedOptionId() !== opt.id"
                            [class.border-gray-200]="selectedOptionId() !== opt.id"
                            [class.text-gray-700]="selectedOptionId() !== opt.id"
                            [class.hover:border-primary-300]="selectedOptionId() !== opt.id"
                            [class.hover:bg-gray-50]="selectedOptionId() !== opt.id">
                            <span>{{ opt.text }}</span>
                            <div class="w-5 h-5 rounded-full border flex items-center justify-center transition-colors"
                                [class.border-primary-500]="selectedOptionId() === opt.id"
                                [class.bg-primary-500]="selectedOptionId() === opt.id">
                                @if (selectedOptionId() === opt.id) {
                                <i class="fa fa-check text-white text-[10px]"></i>
                                }
                            </div>
                        </button>
                        }
                    </div>
                    }

                    <!-- True/False -->
                    @else if (q.questionType === questionTypes.trueFalse) {
                    <div class="grid grid-cols-2 gap-4">
                        <button (click)="selectTrueFalse('true')"
                            class="py-8 border rounded-xl font-bold text-lg text-center transition-all duration-200 flex flex-col items-center justify-center gap-3 focus:outline-none"
                            [class.bg-green-50]="selectedOptionText() === 'true'"
                            [class.border-green-500]="selectedOptionText() === 'true'"
                            [class.text-green-700]="selectedOptionText() === 'true'"
                            [class.bg-white]="selectedOptionText() !== 'true'"
                            [class.border-gray-200]="selectedOptionText() !== 'true'"
                            [class.text-gray-700]="selectedOptionText() !== 'true'"
                            [class.hover:border-green-300]="selectedOptionText() !== 'true'">
                            <i class="fa fa-check-circle text-2xl" [class.text-green-500]="selectedOptionText() === 'true'"></i>
                            <span>True</span>
                        </button>
                        <button (click)="selectTrueFalse('false')"
                            class="py-8 border rounded-xl font-bold text-lg text-center transition-all duration-200 flex flex-col items-center justify-center gap-3 focus:outline-none"
                            [class.bg-red-50]="selectedOptionText() === 'false'"
                            [class.border-red-500]="selectedOptionText() === 'false'"
                            [class.text-red-700]="selectedOptionText() === 'false'"
                            [class.bg-white]="selectedOptionText() !== 'false'"
                            [class.border-gray-200]="selectedOptionText() !== 'false'"
                            [class.text-gray-700]="selectedOptionText() !== 'false'"
                            [class.hover:border-red-300]="selectedOptionText() !== 'false'">
                            <i class="fa fa-times-circle text-2xl" [class.text-red-500]="selectedOptionText() === 'false'"></i>
                            <span>False</span>
                        </button>
                    </div>
                    }

                    <!-- Short Answer / Fill in the Blank -->
                    @else if (q.questionType === questionTypes.shortAnswer || q.questionType === questionTypes.fillInTheBlank) {
                    <div class="flex flex-col gap-2">
                        <label class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Your Answer</label>
                        <textarea [value]="typedAnswer()" (input)="onTypedAnswerChange($event)"
                            rows="4" placeholder="Type your answer here..."
                            autocomplete="off" autocorrect="off" autocapitalize="none" spellcheck="false"
                            class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-700 leading-relaxed">
                        </textarea>
                    </div>
                    }
                </div>

                <!-- Footer Submissions -->
                <div class="px-6 py-4 bg-gray-50 border-t border-gray-150 flex justify-between gap-4">
                    <button (click)="submitAnswer(false, true)"
                        class="px-5 py-2.5 bg-transparent text-gray-500 font-semibold rounded-lg hover:bg-gray-100 hover:text-gray-700 transition-colors">
                        Skip
                    </button>
                    <button (click)="submitAnswer()"
                        [disabled]="q.questionType === questionTypes.multipleChoice ? !selectedOptionId() :
                                   q.questionType === questionTypes.trueFalse ? !selectedOptionText() :
                                   !typedAnswer().trim()"
                        class="px-6 py-2.5 bg-secondary-500 text-white font-semibold rounded-lg shadow hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                        Submit Answer
                    </button>
                </div>
            </div>
            } @else {
            <div class="flex flex-col items-center justify-center py-20 text-gray-400 bg-white rounded-xl shadow-sm border border-gray-200">
                <i class="fa fa-circle-notch fa-spin text-3xl mb-4 text-primary-500"></i>
                <p>Loading question content...</p>
            </div>
            }
            }
        </div>
        }
    </div>
    } @else {
    <div class="flex flex-col items-center justify-center py-20 text-gray-400 bg-white rounded-xl shadow-sm border border-gray-200">
        <i class="fa fa-circle-notch fa-spin text-3xl mb-4 text-primary-500"></i>
        <p>Connecting to live quiz session...</p>
    </div>
    }
</div>
